import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { useOverlay } from './OverlayProvider'

/** Query-only news navigation owns its own focus/scroll; page navigation uses this. */
export function RouteLifecycle() {
  const location = useLocation()
  const navigation = useNavigationType()
  const { close, closeNavigationPanels } = useOverlay()
  const positions = useRef(new Map<string, number>())
  const previousPath = useRef<string | null>(null)
  useEffect(() => {
    const previous = history.scrollRestoration
    history.scrollRestoration = 'manual'
    return () => { history.scrollRestoration = previous }
  }, [])
  useLayoutEffect(() => {
    const pageChanged = previousPath.current !== location.pathname
    if (pageChanged) close()
    else closeNavigationPanels()
    previousPath.current = location.pathname
    const startScroll = window.scrollY
    const frame = requestAnimationFrame(() => {
      if (navigation === 'POP' && positions.current.has(location.key)) {
        window.scrollTo(0, positions.current.get(location.key)!)
        if (pageChanged) document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true })
      } else if (location.hash) {
        let id = location.hash.slice(1)
        try { id = decodeURIComponent(id) } catch { /* A malformed fragment is not a route failure. */ }
        const target = document.getElementById(id)
        target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
        target?.focus({ preventScroll: true })
      } else if (location.state?.smoothScrollTop) {
        window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
        if (pageChanged) document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true })
      } else if (pageChanged) {
        window.scrollTo(0, location.state?.preserveScroll ? startScroll : 0)
        document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true })
      }
    })
    return () => { cancelAnimationFrame(frame); positions.current.set(location.key, window.scrollY) }
  }, [location, navigation, close, closeNavigationPanels])
  return null
}
